export type DeathCause = 'fall' | 'hazard' | 'combat' | 'unknown';

export interface DeathScreenInfo {
  cause: DeathCause;
  impactSpeed: number;
  stage: number;
  lootDropped: number;
  totalDeaths: number;
}

/**
 * Full-screen "You Died" overlay with a Respawn button.
 */
export class DeathScreen {
  private readonly root: HTMLDivElement;
  private readonly titleEl: HTMLElement;
  private readonly detailEl: HTMLElement;
  private readonly lootEl: HTMLElement;
  private readonly button: HTMLButtonElement;
  private readonly boundKey: (e: KeyboardEvent) => void;
  private visible = false;
  private onRespawn: (() => void) | null = null;

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div');
    this.root.id = 'death-screen';
    this.root.innerHTML = `
      <div class="death-panel" role="dialog" aria-label="You died">
        <p class="death-eyebrow">Cave wipeout</p>
        <h1 class="death-title">You Died</h1>
        <p class="death-detail"></p>
        <p class="death-loot"></p>
        <button type="button" class="death-respawn">Respawn</button>
        <p class="death-hint">Confirm · Enter / Space / A</p>
      </div>
    `;
    parent.appendChild(this.root);
    this.titleEl = this.root.querySelector('.death-title') as HTMLElement;
    this.detailEl = this.root.querySelector('.death-detail') as HTMLElement;
    this.lootEl = this.root.querySelector('.death-loot') as HTMLElement;
    this.button = this.root.querySelector('.death-respawn') as HTMLButtonElement;

    this.boundKey = (e: KeyboardEvent) => {
      if (!this.visible) return;
      if (e.code === 'Enter' || e.code === 'Space') {
        e.preventDefault();
        this.triggerRespawn();
      }
    };

    this.button.addEventListener('click', () => this.triggerRespawn());
    window.addEventListener('keydown', this.boundKey);
    this.injectStyles();
  }

  /** Allow InputManager / gamepad confirm to trigger respawn. */
  confirm(): void {
    this.triggerRespawn();
  }

  get isVisible(): boolean {
    return this.visible;
  }

  show(info: DeathScreenInfo, onRespawn: () => void): void {
    this.onRespawn = onRespawn;
    this.visible = true;
    this.root.classList.add('open');

    this.titleEl.textContent = 'You Died';
    this.detailEl.textContent = causeLine(info);
    if (info.lootDropped > 0) {
      this.lootEl.textContent = `Run bag dropped (${info.lootDropped} item${
        info.lootDropped === 1 ? '' : 's'
      }) — recover it after you respawn.`;
    } else {
      this.lootEl.textContent = 'Nothing in the run bag. Gear is still yours.';
    }
    this.button.focus();
  }

  hide(): void {
    this.visible = false;
    this.onRespawn = null;
    this.root.classList.remove('open');
  }

  dispose(): void {
    window.removeEventListener('keydown', this.boundKey);
    this.root.remove();
  }

  private triggerRespawn(): void {
    if (!this.visible) return;
    const cb = this.onRespawn;
    this.hide();
    cb?.();
  }

  private injectStyles(): void {
    if (document.getElementById('death-screen-styles')) return;
    const style = document.createElement('style');
    style.id = 'death-screen-styles';
    style.textContent = `
      #death-screen {
        position: absolute;
        inset: 0;
        z-index: 50;
        display: none;
        place-items: center;
        pointer-events: none;
        background:
          radial-gradient(ellipse at 50% 40%, rgba(80, 18, 18, 0.45), transparent 55%),
          rgba(6, 8, 12, 0.72);
        backdrop-filter: blur(2px);
      }
      #death-screen.open {
        display: grid;
        pointer-events: auto;
      }
      #death-screen .death-panel {
        width: min(420px, calc(100vw - 40px));
        padding: 36px 32px 28px;
        text-align: center;
        border: 1px solid rgba(255, 160, 140, 0.22);
        background: rgba(14, 12, 14, 0.92);
        box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
        animation: death-in 0.35s ease-out;
      }
      @keyframes death-in {
        from { opacity: 0; transform: translateY(10px) scale(0.98); }
        to { opacity: 1; transform: none; }
      }
      #death-screen .death-eyebrow {
        margin: 0 0 8px;
        font-size: 11px;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: rgba(255, 180, 160, 0.7);
      }
      #death-screen .death-title {
        margin: 0 0 12px;
        font-size: 42px;
        font-weight: 700;
        letter-spacing: 0.04em;
        color: #ffb4a2;
        text-shadow: 0 2px 18px rgba(180, 40, 30, 0.45);
      }
      #death-screen .death-detail {
        margin: 0 0 10px;
        font-size: 14px;
        line-height: 1.45;
        color: rgba(232, 238, 245, 0.85);
      }
      #death-screen .death-loot {
        margin: 0 0 22px;
        font-size: 12px;
        line-height: 1.4;
        color: rgba(232, 238, 245, 0.6);
      }
      #death-screen .death-respawn {
        appearance: none;
        border: 1px solid rgba(255, 180, 160, 0.45);
        background: #6a2a28;
        color: #ffe8e0;
        font: inherit;
        font-size: 15px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        padding: 12px 28px;
        cursor: pointer;
        min-width: 160px;
      }
      #death-screen .death-respawn:hover,
      #death-screen .death-respawn:focus-visible {
        filter: brightness(1.12);
        outline: 1px solid rgba(255, 200, 180, 0.55);
      }
      #death-screen .death-hint {
        margin: 12px 0 0;
        font-size: 11px;
        opacity: 0.45;
      }
    `;
    document.head.appendChild(style);
  }
}

function causeLine(info: DeathScreenInfo): string {
  const stage = `Stage ${info.stage}`;
  switch (info.cause) {
    case 'fall':
      return `${stage} · Fell too hard (${info.impactSpeed.toFixed(0)} impact)`;
    case 'combat':
      return `${stage} · Slain by cave fauna`;
    case 'hazard':
      return `${stage} · Claimed by a hazard`;
    default:
      return `${stage} · Lost in the dark`;
  }
}
